import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import styles from "./Card.module.css";
import Skeleton from "./Skeleton";

interface Props {
  children?: ReactNode;
  elevation?: "none" | "1" | "2" | "3" | "4";
  withHover?: boolean;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  scrollable?: boolean;
  onClick?: () => void;
  ref?: ForwardedRef<HTMLDivElement>;
  noPadding?: boolean;
  style?: CSSProperties;
  tabIndex?: number;
}

const SHADOWS = {
  none: "shadow-none",
  1: "shadow-1",
  2: "shadow-2",
  3: "shadow-3",
  4: "shadow-4",
};

const Card = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      elevation = "none",
      className = "",
      disabled,
      loading,
      withHover,
      onClick,
      scrollable,
      noPadding,
      style,
      tabIndex = 0,
    },
    ref
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    };
    return (
      <div
        ref={ref}
        role={"card"}
        style={style}
        className={`
      ${styles.container} 
      ${SHADOWS[elevation]} 
      ${className} 
      ${elevation === "none" ? styles.noElevation : ""}
      ${withHover ? styles.withHover : ""} 
      ${disabled ? styles.disabled : ""}
      ${scrollable ? styles.scrollable : ""}
      ${noPadding ? "" : "p-ds-6"}
      `}
        onClick={onClick}
        onKeyDown={onClick && !disabled ? handleKeyDown : undefined}
        tabIndex={onClick && !disabled ? tabIndex : undefined}
      >
        {!loading ? (
          children
        ) : (
          <div className={styles.loading}>
            <Skeleton width={"100%"} />
            <Skeleton width={"60%"} />
          </div>
        )}
      </div>
    );
  }
);

Card.displayName = "Card";

export default Card;
